/**
 * builderHelpers.js — Shared constants and utility functions for the PageBuilder.
 */

import { Square, Layout, Type, AlignLeft, Image, Play, MousePointer,
  Settings, Plus, Users, MessageSquare, Grid, Edit, Trash2, GripVertical, X, Save,
  ChevronDown
} from 'lucide-react';
import { DEFAULT_COMPONENT_PROPS } from '../../PageRender/layout/LayoutComponent';
import { getComponentDefinition } from '../../componentDefinitions';

// Helper function to get default style values
export const getDefaultStyleValue = (style) => {
  const defaults = {
    fontSize: '16',
    fontWeight: 'normal',
    color: '#1f2937',
    backgroundColor: 'transparent',
    textAlign: 'center',
    borderRadius: '8',
    marginTop: '0',
    marginBottom: '16',
    paddingTop: '0',
    paddingBottom: '0'
  };

  return defaults[style] || '';
};


export const createComponentForPalette = (type) => {
  const id = `${type}-${Date.now()}`;
  switch (type) {
    case 'row':
      return {
        id,
        type: 'row',
        props: { ...DEFAULT_COMPONENT_PROPS.row },
        children: [
          { id: `column-${Date.now()}-1`, type: 'column', props: { ...DEFAULT_COMPONENT_PROPS.column }, children: [] },
          { id: `column-${Date.now()}-2`, type: 'column', props: { ...DEFAULT_COMPONENT_PROPS.column }, children: [] }
        ]
      };
    case 'column':
      return { id, type: 'column', props: { ...DEFAULT_COMPONENT_PROPS.column }, children: [] };
    case 'container':
      return { id, type: 'container', props: { ...DEFAULT_COMPONENT_PROPS.container }, children: [] };
    case 'block':
      return { id, type: 'block', props: { ...getComponentDefinition('block')?.defaultProps }, children: [] };
    case 'text':
      return { id, type: 'text', props: { ...DEFAULT_COMPONENT_PROPS.text, content: 'New text content' } };
    case 'image':
      return { id, type: 'image', props: { ...DEFAULT_COMPONENT_PROPS.image } };
    case 'icon':
      return { id, type: 'icon', props: { ...DEFAULT_COMPONENT_PROPS.icon } };
    case 'button':
      return { id, type: 'button', props: { ...DEFAULT_COMPONENT_PROPS.button } };
    case 'spacer':
      return { id, type: 'spacer', props: { ...DEFAULT_COMPONENT_PROPS.spacer } };
    case 'listItem':
      return { id, type: 'listItem', props: { ...DEFAULT_COMPONENT_PROPS.listItem } };
    case 'video':
      return { id, type: 'video', props: { ...DEFAULT_COMPONENT_PROPS.video } };
    case 'accordion':
      return { id, type: 'accordion', props: { ...DEFAULT_COMPONENT_PROPS.accordion } };
    case 'link':
      return { id, type: 'link', props: { ...DEFAULT_COMPONENT_PROPS.link } };
    case '_quickLayout_2col': {
      const ts = Date.now();
      return {
        id: `row-${ts}`,
        type: 'row',
        props: {
          ...DEFAULT_COMPONENT_PROPS.row,
          width: { base: { value: 100, unit: '%' }, tablet: null, desktop: null },
          padding: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
          margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
        },
        children: [
          { id: `column-${ts}-1`, type: 'column', props: { ...DEFAULT_COMPONENT_PROPS.column, span: { base: 6, tablet: null, desktop: null } }, children: [] },
          { id: `column-${ts}-2`, type: 'column', props: { ...DEFAULT_COMPONENT_PROPS.column, span: { base: 6, tablet: null, desktop: null } }, children: [] },
        ]
      };
    }
    case '_quickLayout_3col': {
      const ts = Date.now();
      return {
        id: `row-${ts}`,
        type: 'row',
        props: {
          ...DEFAULT_COMPONENT_PROPS.row,
          width: { base: { value: 100, unit: '%' }, tablet: null, desktop: null },
          padding: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
          margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
        },
        children: [
          { id: `column-${ts}-1`, type: 'column', props: { ...DEFAULT_COMPONENT_PROPS.column, span: { base: 4, tablet: null, desktop: null } }, children: [] },
          { id: `column-${ts}-2`, type: 'column', props: { ...DEFAULT_COMPONENT_PROPS.column, span: { base: 4, tablet: null, desktop: null } }, children: [] },
          { id: `column-${ts}-3`, type: 'column', props: { ...DEFAULT_COMPONENT_PROPS.column, span: { base: 4, tablet: null, desktop: null } }, children: [] },
        ]
      };
    }
    default:
      return null;
  }
};


export const SECTION_TYPES = {
  // header: {
  //   label: 'Header',
  //   icon: Type,
  //   description: 'Large title text',
  //   defaultContent: {
  //     text: 'Welcome!',
  //     fontSize: '48',
  //     fontWeight: 'bold',
  //     textAlign: 'center',
  //     color: '#1f2937',
  //     sectionBackgroundColor: 'transparent',
  //     marginBottom: '24'
  //   }
  // },
  // paragraph: {
  //   label: 'Paragraph',
  //   icon: AlignLeft,
  //   description: 'Body text content',
  //   defaultContent: {
  //     text: 'Your response has been recorded successfully.',
  //     fontSize: '18',
  //     fontWeight: 'normal',
  //     textAlign: 'center',
  //     color: '#6b7280',
  //     sectionBackgroundColor: 'transparent',
  //     marginBottom: '16'
  //   }
  // },
  // image: {
  //   label: 'Image',
  //   icon: Image,
  //   description: 'Display an image',
  //   defaultContent: {
  //     src: '',
  //     alt: '',
  //     width: '300',
  //     height: 'auto',
  //     borderRadius: '8',
  //     textAlign: 'center',
  //     sectionBackgroundColor: 'transparent',
  //     marginBottom: '24'
  //   }
  // },
  // video: {
  //   label: 'Video',
  //   icon: Play,
  //   description: 'Embed a video',
  //   defaultContent: {
  //     src: '',
  //     width: '560',
  //     height: '315',
  //     autoplay: false,
  //     controls: true,
  //     textAlign: 'center',
  //     sectionBackgroundColor: 'transparent',
  //     marginBottom: '24'
  //   }
  // },
  // cta: {
  //   label: 'Button',
  //   icon: MousePointer,
  //   description: 'Button or link',
  //   defaultContent: {
  //     text: 'Continue',
  //     url: '',
  //     backgroundColor: '#4f46e5',
  //     textColor: '#ffffff',
  //     borderRadius: '8',
  //     padding: '12 24',
  //     fontSize: '16',
  //     fontWeight: 'medium',
  //     buttonSize: 'medium', // small, medium, large
  //     buttonWidth: 'auto', // auto, full
  //     textAlign: 'center',
  //     sectionBackgroundColor: 'transparent',
  //     marginBottom: '16'
  //   }
  // }
};


export const getIconComponent = (iconName) => {
  const iconMap = {
    Type, AlignLeft, Image, Play, MousePointer, Settings, Plus, ChevronDown,
    Users, MessageSquare, Grid, Square, Layout, Edit, Trash2, GripVertical, X, Save
  };
  return iconMap[iconName] || Square; // Default to Square if icon not found
};

// Section editor modal
